<template>
  <div class="ve-split">
    <!-- Left: list of selected tokens -->
    <div class="ve-list-panel">
      <div class="ve-list-header">
        <span class="ve-list-title">{{ selectedTokenIds.length }} token{{ selectedTokenIds.length === 1 ? '' : 's' }}</span>
        <button type="button" class="ve-add-btn" :class="{ 've-add-btn--active': mode === 'add' }" @click="startAdd">+ Add</button>
      </div>
      <div class="ve-list-body">
        <div v-if="selectedTokenIds.length === 0" class="ve-list-empty">No tokens yet</div>
        <div
          v-for="(skinId, idx) in selectedTokenIds"
          :key="skinId"
          class="ve-list-item"
          :class="{ 've-list-item--selected': mode === 'edit' && selectedIdx === idx }"
          @click="selectItem(idx)"
        >
          <img v-if="tokenSkinById(skinId).thumbnailUrl" :src="tokenSkinById(skinId).thumbnailUrl" class="ve-list-thumb" />
          <span class="ve-list-name">{{ (tokenDetails[skinId] || {}).name || tokenSkinById(skinId).name || skinId }}</span>
          <button type="button" class="ve-list-remove" title="Remove" @click.stop="removeToken(skinId)">✕</button>
        </div>
      </div>
    </div>

    <!-- Right: SkinGrid (add) or edit fields (edit) -->
    <div class="ve-detail-panel">
      <!-- ADD MODE -->
      <template v-if="mode === 'add'">
        <p class="ve-hint">Click a skin to add it. Click again to remove it.</p>
        <div v-if="tokensLoading" class="field-desc">Loading token skins…</div>
        <div v-else-if="tokensError" class="field-error">{{ tokensError }}</div>
        <SkinGrid
          v-else
          :skins="tokenSkins"
          :selection-mode="true"
          :selected-ids="selectedTokenIds"
          :show-info="false"
          @toggle="toggleToken"
        />
      </template>

      <!-- EDIT MODE -->
      <template v-else-if="mode === 'edit' && selectedSkinId">
        <div class="ve-edit-form">
          <div class="ve-edit-row-head">
            <img
              v-if="tokenSkinById(selectedSkinId).thumbnailUrl"
              :src="tokenSkinById(selectedSkinId).thumbnailUrl"
              class="ve-edit-thumb"
            />
            <div>
              <div class="ve-edit-skin-name">{{ tokenSkinById(selectedSkinId).name }}</div>
              <div class="ve-edit-hint">Token {{ selectedIdx + 1 }} of {{ selectedTokenIds.length }}</div>
            </div>
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">Name</label>
            <input
              :value="(tokenDetails[selectedSkinId] || {}).name"
              type="text"
              class="field-input"
              @input="onTokenNameInput(selectedSkinId, $event.target.value)"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SkinGrid from '@/components/SkinGrid.vue';

export default {
  name: 'TokensEditor',
  components: { SkinGrid },

  props: {
    value: { type: Array, default: () => [] },
  },

  data() {
    return {
      tokenSkins: [],
      tokensLoading: false,
      tokensError: '',
      selectedTokenIds: [],
      tokenDetails: {},
      mode: 'add',
      selectedIdx: null,
    };
  },

  computed: {
    selectedSkinId() {
      if (this.selectedIdx === null) return null;
      return this.selectedTokenIds[this.selectedIdx] || null;
    },
  },

  async mounted() {
    await this.fetchTokenSkins();
  },

  watch: {
    value(newVal) {
      if (this.tokenSkins.length > 0) {
        this.initFromValue(newVal);
      }
    },
  },

  methods: {
    startAdd() {
      this.mode = 'add';
      this.selectedIdx = null;
    },

    selectItem(idx) {
      this.mode = 'edit';
      this.selectedIdx = idx;
    },

    async fetchTokenSkins() {
      this.tokensLoading = true;
      this.tokensError = '';
      try {
        const res = await fetch(
          '/GetCatalogueSkins?viewMode=public&search=token&perPage=200&page=1&sortOrder=newest',
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        this.tokenSkins = data.result.skins;
        this.$emit('catalogue-loaded', this.tokenSkins);
        if (this.value && this.value.length > 0) {
          this.initFromValue(this.value);
        }
      } catch (err) {
        this.tokensError = 'Could not load token skins: ' + err.message;
      } finally {
        this.tokensLoading = false;
      }
    },

    initFromValue(tokens) {
      const matchedIds = [];
      for (const token of (tokens || [])) {
        let skin;
        if (token.SkinId != null) {
          skin = this.tokenSkins.find(s => String(s.id) === String(token.SkinId));
        }
        if (!skin) {
          skin = this.tokenSkins.find(s => s.name === token.Name);
        }
        if (skin && !matchedIds.includes(String(skin.id))) {
          matchedIds.push(String(skin.id));
          this.$set(this.tokenDetails, String(skin.id), { name: token.Name });
        }
      }
      this.selectedTokenIds = matchedIds;
    },

    tokenSkinById(skinId) {
      return this.tokenSkins.find(s => String(s.id) === skinId) || {};
    },

    toggleToken(skin) {
      const id = String(skin.id);
      const idx = this.selectedTokenIds.indexOf(id);
      if (idx === -1) {
        this.selectedTokenIds.push(id);
        this.$set(this.tokenDetails, id, { name: skin.name || '' });
        // Auto-switch to edit mode for the newly added token
        this.mode = 'edit';
        this.selectedIdx = this.selectedTokenIds.length - 1;
      } else {
        if (this.mode === 'edit' && this.selectedTokenIds[this.selectedIdx] === id) {
          this.mode = 'add';
          this.selectedIdx = null;
        }
        this.selectedTokenIds.splice(idx, 1);
        this.$delete(this.tokenDetails, id);
      }
      this.$emit('input', this.buildTokensJson());
    },

    removeToken(skinId) {
      const idx = this.selectedTokenIds.indexOf(skinId);
      if (idx >= 0) {
        if (this.mode === 'edit' && this.selectedTokenIds[this.selectedIdx] === skinId) {
          this.mode = 'add';
          this.selectedIdx = null;
        }
        this.selectedTokenIds.splice(idx, 1);
        this.$delete(this.tokenDetails, skinId);
      }
      this.$emit('input', this.buildTokensJson());
    },

    onTokenNameInput(skinId, newName) {
      this.$set(this.tokenDetails, skinId, { ...this.tokenDetails[skinId], name: newName });
      this.$emit('input', this.buildTokensJson());
    },

    buildTokensJson() {
      return this.tokenSkins
        .filter(s => this.selectedTokenIds.includes(String(s.id)))
        .map((s, i) => ({
          Id: `token_${i}`,
          SkinId: s.id,
          Name: (this.tokenDetails[String(s.id)] || {}).name || s.name,
          Type: 'Token',
        }));
    },
  },
};
</script>

<style>
/* ── Two-panel layout shared by all 4 visual editors ── */
.ve-split { display: flex; width: 100%; min-height: 400px; }
.ve-list-panel { flex: 0 0 220px; min-width: 180px; display: flex; flex-direction: column; border: 1px solid #30363d; border-radius: 8px 0 0 8px; overflow: hidden; background: #161b22; }
.ve-list-header { padding: 0.55rem 0.75rem; border-bottom: 1px solid #30363d; display: flex; align-items: center; justify-content: space-between; background: #21262d; flex-shrink: 0; }
.ve-list-title { font-size: 0.74rem; color: #8b949e; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.ve-add-btn { padding: 0.25rem 0.6rem; background: #1f6feb; color: #fff; border: none; border-radius: 4px; font-size: 0.78rem; font-weight: 600; cursor: pointer; transition: background 0.15s; }
.ve-add-btn:hover { background: #388bfd; }
.ve-add-btn--active { background: #388bfd; box-shadow: 0 0 0 2px rgba(56,139,253,0.3); }
.ve-list-body { flex: 1; overflow-y: auto; padding: 0.3rem 0; }
.ve-list-empty { padding: 2rem 0.75rem; text-align: center; font-size: 0.8rem; color: #484f58; }
.ve-list-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0.75rem; cursor: pointer; transition: background 0.1s; border-left: 3px solid transparent; }
.ve-list-item:hover { background: rgba(255,255,255,0.04); }
.ve-list-item--selected { background: rgba(31,111,235,0.12); border-left-color: #1f6feb; }
.ve-list-item--disabled { cursor: default; opacity: 0.5; pointer-events: none; }
.ve-list-thumb { width: 30px; height: 30px; object-fit: contain; border-radius: 3px; flex-shrink: 0; background: #0d1117; border: 1px solid #21262d; }
.ve-list-name { flex: 1; font-size: 0.83rem; color: #e6edf3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ve-list-remove { background: none; border: none; color: #484f58; cursor: pointer; padding: 1px 4px; font-size: 0.82rem; border-radius: 3px; flex-shrink: 0; line-height: 1; transition: color 0.1s; }
.ve-list-remove:hover { color: #f85149; }
.ve-detail-panel { flex: 1; min-width: 0; border: 1px solid #30363d; border-left: none; border-radius: 0 8px 8px 0; overflow-y: auto; padding: 1rem 1.25rem; background: #0d1117; }
.ve-hint { font-size: 0.82rem; color: #8b949e; margin: 0 0 0.75rem 0; }
.ve-edit-form { max-width: 480px; }
.ve-edit-row-head { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.25rem; padding-bottom: 1rem; border-bottom: 1px solid #21262d; }
.ve-edit-thumb { width: 64px; height: 64px; object-fit: contain; border-radius: 6px; border: 1px solid #30363d; background: #161b22; flex-shrink: 0; }
.ve-edit-thumb-wide { height: 70px; border-radius: 6px; border: 1px solid #30363d; background: #161b22; flex-shrink: 0; }
.ve-edit-skin-name { font-weight: 600; font-size: 0.95rem; color: #e6edf3; }
.ve-edit-hint { font-size: 0.78rem; color: #8b949e; margin-top: 0.2rem; }
.ve-edit-group { margin-bottom: 1rem; }
.ve-edit-label { display: block; font-size: 0.8rem; font-weight: 600; color: #8b949e; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.35rem; }
</style>
